import React, { useState, useEffect } from 'react';
import { getTemplate } from '../config/index.js';
import RedFlagsSection from './RedFlagsSection';
import UniversalRedFlagsSection from './UniversalRedFlagsSection';
import InstabilitySection from './InstabilitySection';
import NeurologicalSection from './NeurologicalSection';
import StiffnessSection from './StiffnessSection';
import SwellingSection from './SwellingSection';
import ExerciseVitalSignSection from './ExerciseVitalSignSection';
import { 
  DemographicsSection,
  ConsentSection,
  GoalsSection,
  MedicalHistorySection,
  MedicationTriedInput
} from './GenericSections';

// Mobile-responsive styles
const mobileStyles = `
  /* Prevent iOS zoom on input focus - inputs must be 16px+ */
  input, select, textarea {
    font-size: 16px !important;
  }
  
  /* Better touch targets - minimum 44px */
  input[type="radio"], input[type="checkbox"] {
    min-width: 24px;
    min-height: 24px;
  }
  
  /* Full width inputs on mobile */
  @media (max-width: 640px) {
    .form-input, .form-select, .form-textarea {
      width: 100% !important;
      box-sizing: border-box !important;
    }
    
    .nav-buttons {
      flex-direction: column !important;
      gap: 12px !important;
    }
    
    .nav-buttons button {
      width: 100% !important;
    }
    
    .welcome-banner h1 {
      font-size: 24px !important;
    }
    
    .welcome-banner p {
      font-size: 15px !important;
    }
  }
  
  /* Smooth scrolling */
  html {
    scroll-behavior: smooth;
    -webkit-overflow-scrolling: touch;
  }
  
  /* Prevent horizontal scroll */
  body {
    overflow-x: hidden;
  }
`;

// Inject the shared universal red-flag safety screen into every region template
// (signed B2 decision record). It renders directly after the patient details /
// demographics section, before any region-specific content. Any legacy inline
// 'redFlags' section is removed — the universal screen replaces the old
// per-region atomic sets; region-specific supplementary items are rendered by
// their own config sections and scored in the separate supplementary pass.
const withUniversalRedFlags = (templateConfig) => {
  const universalSection = {
    id: 'universalRedFlags',
    title: 'Important Health Questions',
    component: 'UniversalRedFlagsSection',
  };
  // Strip the legacy atomic red-flag section; the universal screen replaces it.
  // Region-specific supplementary items live in a separate 'supplementaryRedFlags'
  // section (Task 3) and are left in place.
  const sections = (templateConfig.sections || []).filter((s) => s.id !== 'redFlags');
  const detailsIdx = sections.findIndex(
    (s) => s.component === 'DemographicsSection' || s.component === 'YourDetailsSection'
  );
  const consentIdx = sections.findIndex((s) => s.component === 'ConsentSection');
  const insertAfter = detailsIdx >= 0 ? detailsIdx : consentIdx;
  const insertAt = insertAfter >= 0 ? insertAfter + 1 : 0;
  const next = [...sections];
  next.splice(insertAt, 0, universalSection);
  return { ...templateConfig, sections: next };
};

const DynamicPatientForm = ({ injuryType, onSubmit, isSubmitting = false, initialData = {} }) => {
  const [template, setTemplate] = useState(null);
  const [formData, setFormData] = useState(initialData);
  const [currentSection, setCurrentSection] = useState(0);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadTemplate = async () => {
      setLoading(true);
      try {
        const templateConfig = getTemplate(injuryType);
        if (!templateConfig) {
          throw new Error(`No template found for injury type: ${injuryType}`);
        }
        setTemplate(withUniversalRedFlags(templateConfig));
      } catch (error) {
        console.error('Error loading template:', error);
      } finally {
        setLoading(false);
      }
    };

    loadTemplate();
  }, [injuryType]);

  const handleFieldChange = (fieldId, value) => {
    setFormData(prev => ({
      ...prev,
      [fieldId]: value
    }));
    
    if (errors[fieldId]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fieldId];
        return newErrors;
      });
    }
  };

  // Calculate patient age from DOB (used for age-gated fields)
  const getPatientAge = () => {
    const dob = formData.dateOfBirth;
    if (!dob) return null;
    const today = new Date();
    const birth = new Date(dob);
    let age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
    return age;
  };

  // Check if a conditional field should be shown
  const shouldShowField = (fieldConfig, fieldId) => {
    // Age-based hiding: smoking under 16, alcohol under 18
    const age = getPatientAge();
    if (age !== null) {
      if ((fieldId === 'smoking' || fieldId === 'smokingStatus') && age < 16) return false;
      if (fieldId === 'alcoholUse' && age < 18) return false;
    }

    if (!fieldConfig.showWhen) return true;

    const { field, includes, equals } = fieldConfig.showWhen;
    const fieldValue = formData[field];

    // Handle 'equals' condition
    if (equals !== undefined) {
      return fieldValue === equals;
    }

    // Handle 'includes' condition
    if (includes !== undefined) {
      if (Array.isArray(fieldValue)) {
        return fieldValue.includes(includes);
      }
      return fieldValue === includes;
    }

    return true;
  };

  const validateSection = (section) => {
    const sectionErrors = {};
    const sectionFields = section.fields || [];
    
    sectionFields.forEach(fieldId => {
      const fieldConfig = template.fields[fieldId];
      if (fieldConfig && fieldConfig.required) {
        // Skip validation for conditional fields or age-gated fields that aren't shown
        if (!shouldShowField(fieldConfig, fieldId)) {
          return;
        }
        
        const value = formData[fieldId];
        if (!value || (Array.isArray(value) && value.length === 0)) {
          sectionErrors[fieldId] = 'This field is required';
        }
      }
    });

    return sectionErrors;
  };

  const validateConsentSection = (section) => {
    if (section.component === 'ConsentSection' && formData.consentAI === undefined) {
      return { consentAI: 'Please select Yes or No for AI technology consent' };
    }
    return {};
  };

  const handleNext = () => {
    const currentSectionConfig = template.sections[currentSection];
    const sectionErrors = {
      ...validateSection(currentSectionConfig),
      ...validateConsentSection(currentSectionConfig)
    };

    if (Object.keys(sectionErrors).length > 0) {
      setErrors(sectionErrors);
      alert(sectionErrors.consentAI || 'Please complete all required fields');
      return;
    }

    if (currentSection < template.sections.length - 1) {
      setCurrentSection(currentSection + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handlePrevious = () => {
    if (currentSection > 0) {
      setCurrentSection(currentSection - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
  
    console.log('Submit clicked, formData:', formData);
  
    let allErrors = {};
    template.sections.forEach(section => {
      const sectionErrors = validateSection(section);
      const consentErrors = validateConsentSection(section);
      allErrors = { ...allErrors, ...sectionErrors, ...consentErrors };
    });

    if (Object.keys(allErrors).length > 0) {
      setErrors(allErrors);
      console.log('Validation errors:', allErrors);
      alert('Please complete all required fields before submitting');
      return;
    }

    console.log('Calling onSubmit with formData');
    onSubmit(formData);
  };

  // Common input styles - mobile friendly with 16px font to prevent iOS zoom
  const inputStyle = {
    width: '100%',
    padding: '14px 12px',
    border: '2px solid #9ca3af',
    borderRadius: '8px',
    fontSize: '16px',
    fontFamily: 'inherit',
    boxSizing: 'border-box',
    WebkitAppearance: 'none',
    appearance: 'none'
  };

  const labelStyle = {
    display: 'block',
    fontSize: '15px',
    fontWeight: '500',
    color: '#374151',
    marginBottom: '8px'
  };

  const radioOptionStyle = (isSelected) => ({
    display: 'flex',
    alignItems: 'center',
    padding: '14px 12px',
    border: `2px solid ${isSelected ? '#2563eb' : '#d1d5db'}`,
    borderRadius: '8px',
    cursor: 'pointer',
    backgroundColor: isSelected ? '#eff6ff' : 'white',
    transition: 'all 0.2s',
    minHeight: '48px' // Touch-friendly
  });

  const checkboxStyle = {
    marginRight: '12px',
    width: '24px',
    height: '24px',
    cursor: 'pointer',
    flexShrink: 0
  };

  const renderSection = (section) => {
    switch(section.component) {
      case 'DemographicsSection':
      case 'YourDetailsSection':
        return (
          <DemographicsSection
            config={section}
            formData={formData}
            onChange={handleFieldChange}
          />
        );
      case 'ConsentSection':
        return (
          <ConsentSection
            config={section}
            formData={formData}
            onChange={handleFieldChange}
          />
        );
      case 'GoalsSection':
        return (
          <GoalsSection
            config={section}
            formData={formData}
            onChange={handleFieldChange}
          />
        );
      case 'UniversalRedFlagsSection':
        return (
          <UniversalRedFlagsSection
            injuryType={injuryType}
            formData={formData}
            onChange={handleFieldChange}
          />
        );
      case 'RedFlagsSection':
        return (
          <RedFlagsSection
            config={section}
            formData={formData}
            onChange={handleFieldChange}
          />
        );
      case 'InstabilitySection':
        return (
          <InstabilitySection
            config={section}
            formData={formData}
            onChange={handleFieldChange}
            injuryType={injuryType}
          />
        );
      case 'NeurologicalSection':
        return (
          <NeurologicalSection
            config={section}
            formData={formData}
            onChange={handleFieldChange}
          />
        );
      case 'StiffnessSection':
        return (
          <StiffnessSection
            config={section}
            formData={formData}
            onChange={handleFieldChange}
          />
        );
      case 'SwellingSection':
        return (
          <SwellingSection
            config={section}
            formData={formData}
            onChange={handleFieldChange}
          />
        );
      case 'MedicalHistorySection':
        return (
          <MedicalHistorySection
            formData={formData}
            onChange={handleFieldChange}
            additionalFields={section.additionalFields}
          />
        );
      case 'ExerciseVitalSignSection':
        return (
          <ExerciseVitalSignSection
            formData={formData}
            onChange={handleFieldChange}
            patientAge={formData.age || null}
          />
        );
      default:
        return (
          <div>
            {section.fields && section.fields.map((field, idx) => {
              let fieldId, fieldConfig;
              
              if (typeof field === 'object' && field.id) {
                fieldId = field.id;
                fieldConfig = field;
              } else {
                fieldId = field;
                fieldConfig = template.fields[fieldId];
                if (!fieldConfig) return null;
              }
              
              // Check if conditional or age-gated field should be shown
              if (!shouldShowField(fieldConfig, fieldId)) {
                return null;
              }
              
              return (
                <div key={fieldId} style={{ marginBottom: '24px' }}>
                  <label style={labelStyle}>
                    {fieldConfig.label}
                    {fieldConfig.required && <span style={{ color: '#dc2626', marginLeft: '4px' }}>*</span>}
                  </label>
                  
                  {fieldConfig.type === 'textarea' && fieldId.toLowerCase().includes('medication') ? (
                    <MedicationTriedInput
                      value={formData[fieldId] || ''}
                      onChange={(val) => handleFieldChange(fieldId, val)}
                      placeholder={fieldConfig.placeholder}
                    />
                  ) : fieldConfig.type === 'textarea' ? (
                    <textarea
                      value={formData[fieldId] || ''}
                      onChange={(e) => handleFieldChange(fieldId, e.target.value)}
                      placeholder={fieldConfig.placeholder}
                      rows={fieldConfig.rows || 3}
                      style={{
                        ...inputStyle,
                        resize: 'vertical',
                        minHeight: '100px'
                      }}
                    />
                  ) : fieldConfig.type === 'radio' ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      {fieldConfig.options && fieldConfig.options.map(option => (
                        <label 
                          key={option} 
                          style={radioOptionStyle(formData[fieldId] === option)}
                        >
                          <input
                            type="radio"
                            name={fieldId}
                            value={option}
                            checked={formData[fieldId] === option}
                            onChange={(e) => handleFieldChange(fieldId, e.target.value)}
                            style={checkboxStyle}
                          />
                          <span style={{ fontSize: '16px', color: '#374151' }}>{option}</span>
                        </label>
                      ))}
                    </div>
                  ) : fieldConfig.type === 'checkbox-multiple' ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      {fieldConfig.options && fieldConfig.options.map(option => (
                        <label 
                          key={option} 
                          style={radioOptionStyle((formData[fieldId] || []).includes(option))}
                        >
                          <input
                            type="checkbox"
                            checked={(formData[fieldId] || []).includes(option)}
                            onChange={(e) => {
                              const current = formData[fieldId] || [];
                              const updated = e.target.checked
                                ? [...current, option]
                                : current.filter(v => v !== option);
                              handleFieldChange(fieldId, updated);
                            }}
                            style={checkboxStyle}
                          />
                          <span style={{ fontSize: '16px', color: '#374151' }}>{option}</span>
                        </label>
                      ))}
                    </div>
                  ) : fieldConfig.type === 'date' ? (
                    <input
                      type="date"
                      value={formData[fieldId] || ''}
                      onChange={(e) => handleFieldChange(fieldId, e.target.value)}
                      style={inputStyle}
                    />
                  ) : (
                    <input
                      type={fieldConfig.type || 'text'}
                      value={formData[fieldId] || ''}
                      onChange={(e) => handleFieldChange(fieldId, e.target.value)}
                      placeholder={fieldConfig.placeholder}
                      style={inputStyle}
                    />
                  )}
                  
                  {fieldConfig.note && (
                    <p style={{ fontSize: '13px', color: '#6b7280', marginTop: '6px', fontStyle: 'italic' }}>
                      {fieldConfig.note}
                    </p>
                  )}
                  
                  {errors[fieldId] && (
                    <p style={{ fontSize: '14px', color: '#dc2626', marginTop: '6px' }}>
                      {errors[fieldId]}
                    </p>
                  )}
                </div>
              );
            })}

            {section.activities && section.activities.length > 0 && (
              <div style={{ marginBottom: '24px' }}>
                <h3 style={{ fontWeight: '600', marginBottom: '12px', fontSize: '16px' }}>Affected Activities:</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {section.activities.map((activity, idx) => (
                    <label 
                      key={idx} 
                      style={radioOptionStyle(formData[`activity_${idx}`])}
                    >
                      <input
                        type="checkbox"
                        checked={formData[`activity_${idx}`] || false}
                        onChange={(e) => handleFieldChange(`activity_${idx}`, e.target.checked)}
                        style={checkboxStyle}
                      />
                      <span style={{ fontSize: '16px' }}>{activity}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {section.additionalQuestions && section.additionalQuestions.map((q) => (
              <div key={q.id} style={{ marginBottom: '24px' }}>
                <label style={labelStyle}>{q.label}</label>
                {q.type === 'textarea' ? (
                  <textarea
                    value={formData[q.id] || ''}
                    onChange={(e) => handleFieldChange(q.id, e.target.value)}
                    placeholder={q.placeholder}
                    rows={q.rows || 3}
                    style={{
                      ...inputStyle,
                      resize: 'vertical',
                      minHeight: '100px'
                    }}
                  />
                ) : q.type === 'radio' && q.options ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {q.options.map(option => (
                      <label 
                        key={option} 
                        style={radioOptionStyle(formData[q.id] === option)}
                      >
                        <input
                          type="radio"
                          name={q.id}
                          value={option}
                          checked={formData[q.id] === option}
                          onChange={(e) => handleFieldChange(q.id, e.target.value)}
                          style={checkboxStyle}
                        />
                        <span style={{ fontSize: '16px' }}>{option}</span>
                      </label>
                    ))}
                  </div>
                ) : (
                  <input
                    type="text"
                    value={formData[q.id] || ''}
                    onChange={(e) => handleFieldChange(q.id, e.target.value)}
                    placeholder={q.placeholder}
                    style={inputStyle}
                  />
                )}
              </div>
            ))}

            {section.specificQuestions && section.specificQuestions.map((q) => (
              <div key={q.id} style={{ marginBottom: '24px' }}>
                <label style={labelStyle}>{q.label}</label>
                {q.type === 'textarea' ? (
                  <textarea
                    value={formData[q.id] || ''}
                    onChange={(e) => handleFieldChange(q.id, e.target.value)}
                    placeholder={q.placeholder}
                    rows={q.rows || 3}
                    style={{
                      ...inputStyle,
                      resize: 'vertical',
                      minHeight: '100px'
                    }}
                  />
                ) : q.type === 'radio' && q.options ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {q.options.map(option => (
                      <label 
                        key={option} 
                        style={radioOptionStyle(formData[q.id] === option)}
                      >
                        <input
                          type="radio"
                          name={q.id}
                          value={option}
                          checked={formData[q.id] === option}
                          onChange={(e) => handleFieldChange(q.id, e.target.value)}
                          style={checkboxStyle}
                        />
                        <span style={{ fontSize: '16px' }}>{option}</span>
                      </label>
                    ))}
                  </div>
                ) : (
                  <input
                    type="text"
                    value={formData[q.id] || ''}
                    onChange={(e) => handleFieldChange(q.id, e.target.value)}
                    placeholder={q.placeholder}
                    style={inputStyle}
                  />
                )}
              </div>
            ))}

            {section.categories && section.categories.map((cat) => (
              <div key={cat.id} style={{ 
                marginBottom: '24px', 
                padding: '16px', 
                backgroundColor: '#f9fafb', 
                borderRadius: '8px',
                border: '1px solid #e5e7eb'
              }}>
                <label style={{ ...labelStyle, marginBottom: '12px' }}>{cat.label}</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {cat.options && cat.options.map(option => (
                    <label 
                      key={option} 
                      style={radioOptionStyle((formData[cat.id] || []).includes(option))}
                    >
                      <input
                        type="checkbox"
                        checked={(formData[cat.id] || []).includes(option)}
                        onChange={(e) => {
                          const current = formData[cat.id] || [];
                          const updated = e.target.checked
                            ? [...current, option]
                            : current.filter(v => v !== option);
                          handleFieldChange(cat.id, updated);
                        }}
                        style={checkboxStyle}
                      />
                      <span style={{ fontSize: '16px' }}>{option}</span>
                    </label>
                  ))}
                </div>
                {cat.note && (
                  <p style={{ fontSize: '13px', color: '#6b7280', marginTop: '10px', fontStyle: 'italic' }}>
                    {cat.note}
                  </p>
                )}
              </div>
            ))}

            {!section.fields && !section.activities && !section.additionalQuestions && 
             !section.specificQuestions && !section.categories && (
              <div style={{ marginBottom: '16px' }}>
                <textarea
                  value={formData[`section_${section.id}`] || ''}
                  onChange={(e) => handleFieldChange(`section_${section.id}`, e.target.value)}
                  placeholder={`Please provide details about ${section.title.replace(/^\d+\.\s*/, '').toLowerCase()}`}
                  rows={5}
                  style={{
                    ...inputStyle,
                    resize: 'vertical',
                    minHeight: '120px'
                  }}
                />
              </div>
            )}
          </div>
        );
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '48px 16px' }}>
        <div style={{ 
          width: '40px', 
          height: '40px', 
          border: '3px solid #e5e7eb', 
          borderTopColor: '#2563eb', 
          borderRadius: '50%', 
          animation: 'spin 0.8s linear infinite',
          margin: '0 auto 16px'
        }} />
        <p style={{ color: '#6b7280', fontSize: '16px' }}>Loading form...</p>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (!template) {
    return (
      <div style={{ textAlign: 'center', padding: '48px 16px', color: '#dc2626' }}>
        Error: Could not load template for {injuryType}
      </div>
    );
  }

  const currentSectionConfig = template.sections[currentSection];
  const progress = ((currentSection + 1) / template.sections.length) * 100;

  return (
    <div style={{ maxWidth: '800px', margin: '0 auto' }}>
      {/* Inject mobile styles */}
      <style>{mobileStyles}</style>
      
      {/* Progress Bar */}
      <div style={{ marginBottom: '24px' }}>
        <div style={{ 
          backgroundColor: '#e5e7eb', 
          borderRadius: '9999px', 
          height: '8px',
          overflow: 'hidden'
        }}>
          <div
            style={{ 
              backgroundColor: '#2563eb', 
              height: '8px', 
              borderRadius: '9999px',
              transition: 'width 0.3s ease',
              width: `${progress}%`
            }}
          />
        </div>
        <p style={{ 
          textAlign: 'center', 
          fontSize: '14px', 
          color: '#6b7280', 
          marginTop: '8px' 
        }}>
          Section {currentSection + 1} of {template.sections.length}
        </p>
      </div>

      {/* Welcome Banner - only on first section */}
      {currentSection === 0 && (
        <div 
          className="welcome-banner"
          style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            padding: '24px 20px',
            borderRadius: '12px',
            marginBottom: '24px',
            textAlign: 'center'
          }}
        >
          <h1 style={{ fontSize: '26px', fontWeight: 'bold', margin: '0 0 12px 0', lineHeight: '1.3' }}>
            Kia ora, Welcome to ClinHx
          </h1>
          <p style={{ fontSize: '15px', margin: 0, opacity: 0.95, lineHeight: '1.6' }}>
            Please complete the following sections to help us provide you with the best possible care. 
            If there are any sections you are not able to complete feel free to leave them blank. 
            If you have any concerns about completing this form then please contact our reception team 
            at cambridge@angsm.co.nz or on 07 957 6064
          </p>
        </div>
      )}

      {/* Section Content */}
      <div style={{ marginBottom: '24px' }}>
        <h2 style={{ 
          fontSize: '22px', 
          fontWeight: 'bold', 
          marginBottom: '20px',
          color: '#1f2937',
          lineHeight: '1.3'
        }}>
          {currentSectionConfig.title}
        </h2>
        {renderSection(currentSectionConfig)}
      </div>

      {/* Navigation Buttons - Mobile Friendly */}
      <div 
        className="nav-buttons"
        style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          gap: '16px',
          marginTop: '32px',
          paddingBottom: '20px'
        }}
      >
        <button
          type="button"
          onClick={handlePrevious}
          disabled={currentSection === 0}
          style={{
            flex: 1,
            padding: '16px 20px',
            backgroundColor: currentSection === 0 ? '#e5e7eb' : '#f3f4f6',
            color: currentSection === 0 ? '#9ca3af' : '#374151',
            borderRadius: '10px',
            border: 'none',
            cursor: currentSection === 0 ? 'not-allowed' : 'pointer',
            fontSize: '16px',
            fontWeight: '600',
            minHeight: '52px',
            transition: 'all 0.2s'
          }}
        >
          Previous
        </button>

        {currentSection < template.sections.length - 1 ? (
          <button
            type="button"
            onClick={handleNext}
            style={{
              flex: 1,
              padding: '16px 20px',
              backgroundColor: '#2563eb',
              color: 'white',
              borderRadius: '10px',
              border: 'none',
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: '600',
              minHeight: '52px',
              transition: 'all 0.2s'
            }}
          >
            Next
          </button>
        ) : (
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitting}
            style={{
              flex: 1,
              padding: '16px 20px',
              backgroundColor: isSubmitting ? '#86efac' : '#16a34a',
              color: 'white',
              borderRadius: '10px',
              border: 'none',
              cursor: isSubmitting ? 'not-allowed' : 'pointer',
              fontSize: '16px',
              fontWeight: '600',
              minHeight: '52px',
              transition: 'all 0.2s',
              opacity: isSubmitting ? 0.7 : 1
            }}
          >
            {isSubmitting ? 'Submitting...' : 'Submit'}
          </button>
        )}
      </div>
    </div>
  );
};

export default DynamicPatientForm;
